import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import API_CONFIG from '../config/api';
import { apiFetch, getToken, setToken } from '../api/client';
import { getUnitStorageJSON, setUnitStorageJSON } from '../utils/storageByUnit';

/**
 * ============================================================================
 * SGC — AuthContext (RBAC + sessão)
 * ----------------------------------------------------------------------------
 * SEGURANÇA / LGPD (front-end):
 * - O contexto NÃO armazena senha nem tokens de terceiros além do fluxo de
 *   sessão já existente (token via api/client). Guarde apenas identidade
 *   mínima: id, nome, email, tipo, cargo, setor, unidade/role de sessão.
 * - Soft Delete: o sistema opera 100% com Soft Delete. Nenhuma requisição de
 *   exclusão no front-end deve ser tratada como Hard Delete — remoções são
 *   lógicas (ex.: flag ativa/excluido_em). NÃO apague registos do banco via UI.
 * - RBAC no front é UX/controle de menu e rotas. O backend DEVE validar
 *   permissões independentemente; nunca confiar só no cliente.
 * - Sem duplicação: a role canónica vem de getUserRole(user). Flags legadas
 *   (isAdmin, isFiscal, …) são aliases derivados — não um segundo motor.
 * ============================================================================
 */

const ADMINS_KEY = 'admins';
const DEFAULT_ADMINS = [
  'rafael.santana.cs@isgsaude.org',
];

/** Único SUPER_ADMIN (hardcode de identificação do admin mestre). */
export const SUPER_ADMIN_EMAIL = 'rafael.santana.cs@isgsaude.org';

/** Roles canónicas do SGC (Fase 1 RBAC). */
export const ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  GLOBAL_VIEWER: 'GLOBAL_VIEWER',
  LOCAL_APPROVER: 'LOCAL_APPROVER',
  LOCAL_ADMIN: 'LOCAL_ADMIN',
  LOCAL_OPERATOR: 'LOCAL_OPERATOR',
  FINANCEIRO: 'FINANCEIRO',
  /** Coordenador Financeiro: fiscal do Financeiro + monitor (leitura) de todos os setores. */
  FISCAL_MONITOR: 'FISCAL_MONITOR',
  /** Analista Financeiro: leitura de contratos/NF de todos os setores + ações financeiras. */
  FINANCEIRO_ANALISTA: 'FINANCEIRO_ANALISTA',
  FISCAL: 'FISCAL',
  SECTOR_VIEWER: 'SECTOR_VIEWER',
};

const FISCAIS_ESPECIAIS = {
  'luiz.fernando.cs@isgsaude.org': 'fiscal_financeiro',
  'gadm.cs@isgsaude.org': 'fiscal_gerencia_adm',
};

function normalizeEmail(email) {
  return (email || '').trim().toLowerCase();
}

function normalizeText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, ' ');
}

/** Cargo/tipo: Coordenador Financeiro (antes do match genérico "coordenador"). */
export function isCoordenadorFinanceiroUser(user) {
  if (!user) return false;
  const tipo = normalizeText(user.tipo);
  const cargo = normalizeText(user.cargo);
  return (
    tipo === 'coordenador financeiro' ||
    cargo === 'coordenador financeiro' ||
    `${tipo} ${cargo}`.includes('coordenador financeiro')
  );
}

/** Cargo/tipo: Analista Financeiro (antes do match genérico "analista"). */
export function isAnalistaFinanceiroUser(user) {
  if (!user) return false;
  const tipo = normalizeText(user.tipo);
  const cargo = normalizeText(user.cargo);
  return (
    tipo === 'analista financeiro' ||
    cargo === 'analista financeiro' ||
    `${tipo} ${cargo}`.includes('analista financeiro')
  );
}

/** Leitura ampla de contratos/NF na unidade (sem trava de setor). */
export function canMonitorAllSetoresRole(role) {
  return (
    role === ROLES.FISCAL_MONITOR ||
    role === ROLES.FINANCEIRO_ANALISTA ||
    role === ROLES.FINANCEIRO ||
    role === ROLES.LOCAL_ADMIN ||
    role === ROLES.LOCAL_APPROVER ||
    role === ROLES.GLOBAL_VIEWER ||
    role === ROLES.SUPER_ADMIN
  );
}

/**
 * Deriva a role canónica a partir de email + cargo/tipo (sem estado duplicado).
 * Ordem: SUPER_ADMIN → cargos financeiros específicos → fiscal → financeiro →
 * corporativo → diretoria → admin/coordenação → operadores → fallback SECTOR_VIEWER.
 */
export function getUserRole(user) {
  if (!user) return null;

  const email = normalizeEmail(user.email);
  const tipo = normalizeText(user.tipo);
  const cargo = normalizeText(user.cargo);
  const blob = `${tipo} ${cargo}`;
  const sessionRole = user.role;

  if (email === SUPER_ADMIN_EMAIL) return ROLES.SUPER_ADMIN;

  // Antes de "coordenador"/"analista" genéricos — cargos do setor Financeiro
  if (isCoordenadorFinanceiroUser(user)) return ROLES.FISCAL_MONITOR;
  if (isAnalistaFinanceiroUser(user)) return ROLES.FINANCEIRO_ANALISTA;

  if (tipo === 'fiscal' || FISCAIS_ESPECIAIS[email]) return ROLES.FISCAL;

  if (tipo === 'financeiro') return ROLES.FINANCEIRO;

  // Diretorias / gerência corporativas → GLOBAL_VIEWER (todas as unidades)
  if (
    tipo === 'diretor tecnico' ||
    tipo === 'diretor financeiro' ||
    tipo === 'diretor presidente' ||
    tipo === 'gerente corporativo' ||
    blob.includes('corporativa') ||
    blob.includes('corporativo')
  ) {
    return ROLES.GLOBAL_VIEWER;
  }

  // Diretoria da unidade (não corporativa)
  const isDiretoriaUnidade =
    tipo === 'diretoria executiva' ||
    (blob.includes('diretor') && !blob.includes('corporativ'));

  if (isDiretoriaUnidade) {
    return ROLES.LOCAL_APPROVER;
  }

  if (blob.includes('gerencia operacional') || blob.includes('gerência operacional')) {
    return ROLES.LOCAL_APPROVER;
  }

  // Admin de unidade (não é o SUPER_ADMIN) ou coordenação/supervisão de Contratos
  if (
    tipo === 'admin' ||
    sessionRole === 'ADMIN' ||
    tipo === 'coordenador' ||
    tipo === 'supervisor' ||
    blob.includes('coordenador') ||
    blob.includes('supervisor')
  ) {
    return ROLES.LOCAL_ADMIN;
  }

  if (
    tipo === 'analista' ||
    tipo === 'assistente' ||
    tipo === 'auxiliar' ||
    blob.includes('analista') ||
    blob.includes('assistente') ||
    blob.includes('auxiliar')
  ) {
    return ROLES.LOCAL_OPERATOR;
  }

  // Usuário genérico: só visualiza o setor cadastrado
  return ROLES.SECTOR_VIEWER;
}

/** Rank para unir permissões próprias + de titular(es) substituídos (ATIVA). */
const ROLE_RANK = {
  [ROLES.SECTOR_VIEWER]: 1,
  [ROLES.FISCAL]: 2,
  [ROLES.FINANCEIRO]: 2,
  [ROLES.LOCAL_OPERATOR]: 3,
  [ROLES.FINANCEIRO_ANALISTA]: 3,
  [ROLES.FISCAL_MONITOR]: 4,
  [ROLES.LOCAL_ADMIN]: 5,
  [ROLES.LOCAL_APPROVER]: 6,
  [ROLES.GLOBAL_VIEWER]: 7,
  [ROLES.SUPER_ADMIN]: 99,
};

export function getEffectiveUserRole(user) {
  const own = getUserRole(user);
  const ativos = Array.isArray(user?.substituicoesAtivas) ? user.substituicoesAtivas : [];
  if (!ativos.length) return own;
  let best = own;
  let bestRank = ROLE_RANK[own] || 0;
  for (const t of ativos) {
    const r = getUserRole({
      tipo: t.titularTipo,
      cargo: t.titularCargo || t.cargoAssumido,
      email: t.titularEmail,
    });
    const rank = ROLE_RANK[r] || 0;
    if (rank > bestRank) {
      best = r;
      bestRank = rank;
    }
  }
  return best;
}

/**
 * Escolhe a unidade da sessão.
 * - Sem multi-unidade: sempre a do cadastro (tenantCodigo), nunca a 1ª da lista alfabética.
 * - Com multi-unidade: pode restaurar a última selecionada.
 */
function pickUnidadeDaLista(list, { tenantCodigo, canAccessAllUnidades = false } = {}) {
  if (!Array.isArray(list) || list.length === 0) return null;
  const codigo = tenantCodigo ? String(tenantCodigo).trim() : '';
  if (codigo) {
    const byTenant = list.find(
      (u) => String(u.codigo || '').trim() === codigo || String(u.id) === codigo
    );
    if (byTenant) return byTenant;
  }
  if (canAccessAllUnidades) {
    const saved = localStorage.getItem('unidadeSelecionada');
    if (saved) {
      const found = list.find((u) => u.codigo === saved || u.id === parseInt(saved, 10));
      if (found) return found;
    }
  }
  return list[0];
}

const AuthContext = createContext({
  user: null,
  sessionLoading: true,
  rbacRole: null,
  isAdmin: false,
  isFinanceiro: false,
  isFiscal: false,
  isCargoCorporativo: false,
  cargoCorporativo: null,
  fiscalSetor: null,
  fotoPerfil: null,
  assinatura: null,
  admins: [],
  unidades: [],
  unidadeSelecionada: null,
  setUnidadeSelecionada: () => {},
  login: async () => false,
  logout: () => {},
  addAdmin: async () => {},
  removeAdmin: async () => {},
  atualizarAssinatura: () => {},
  getUserRole: () => null,
  isGlobalUser: () => false,
  canAuthorize: () => false,
  canAuthorizeContratacao: () => false,
  hasPermission: () => false,
  canViewAllNotas: () => false,
  canEditNota: () => false,
  canAddComprovante: () => false,
  canAddAvaliacao: () => false,
  canEditContratos: () => false,
  canEditPrestadores: () => false,
  canAccessConfiguracao: () => false,
  atualizarFotoPerfil: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [admins, setAdmins] = useState(DEFAULT_ADMINS);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isFinanceiro, setIsFinanceiro] = useState(false);
  const [isFiscal, setIsFiscal] = useState(false);
  const [isCargoCorporativo, setIsCargoCorporativo] = useState(false);
  const [cargoCorporativo, setCargoCorporativo] = useState(null);
  const [fiscalSetor, setFiscalSetor] = useState(null);
  const [fotoPerfil, setFotoPerfil] = useState(null);
  const [assinatura, setAssinatura] = useState(null);

  const [isFiscalEspecial, setIsFiscalEspecial] = useState(false);
  const [isFiscalFinanceiro, setIsFiscalFinanceiro] = useState(false);
  const [isFiscalGerenciaAdm, setIsFiscalGerenciaAdm] = useState(false);
  const [isCoordenadorFinanceiro, setIsCoordenadorFinanceiro] = useState(false);
  const [isAnalistaFinanceiro, setIsAnalistaFinanceiro] = useState(false);

  const [unidades, setUnidades] = useState([]);
  const [unidadeSelecionada, setUnidadeSelecionada] = useState(null);

  const rbacRole = useMemo(() => getEffectiveUserRole(user), [user]);
  const substituicoesAtivas = useMemo(
    () => (Array.isArray(user?.substituicoesAtivas) ? user.substituicoesAtivas : []),
    [user]
  );
  const substituindoLabel = useMemo(() => {
    if (!substituicoesAtivas.length) return null;
    const t = substituicoesAtivas[0];
    return `Substituindo ${t.titularNome || 'colaborador'} até ${t.dataFimBr || t.dataFim || '—'}`;
  }, [substituicoesAtivas]);

  const verificarCargoCorporativo = (tipo, cargo) => {
    const cargosCorporativos = [
      'gerente_corporativo',
      'diretor_financeiro',
      'diretor_tecnico'
    ];
    return cargosCorporativos.includes(tipo) ||
      (cargo && cargosCorporativos.some(cc => cargo.toLowerCase().includes(cc.replace('_', ' '))));
  };

  const verificarFiscaisEspeciais = (email) => {
    return FISCAIS_ESPECIAIS[normalizeEmail(email)] || null;
  };

  const buscarFotoPerfil = async (userId) => {
    if (!userId) return;
    try {
      const response = await apiFetch(`/api/usuarios/${userId}/foto`);
      if (response.ok) {
        const data = await response.json();
        setFotoPerfil(data.fotoPerfil);
      } else if (response.status === 404) {
        setFotoPerfil(null);
      }
    } catch (error) {
      console.warn('Foto de perfil: falha ao buscar (mantendo valor anterior):', error?.message || error);
    }
  };

  /**
   * Restaura sessão sem alterar o contrato da API.
   * Mescla tipo/cargo/setor do localStorage (vindos do login) porque /api/me
   * ainda não devolve esses campos — evita perda de identidade RBAC no refresh.
   */
  const loadSessionAndUnidades = useCallback(async () => {
    setSessionLoading(true);
    const token = getToken();
    if (!token) {
      setUser(null);
      localStorage.removeItem('user');
      setSessionLoading(false);
      return;
    }
    try {
      const meRes = await apiFetch('/api/me');
      if (!meRes.ok) {
        setToken(null);
        setUser(null);
        setSessionLoading(false);
        return;
      }
      const me = await meRes.json();
      let cached = null;
      try {
        cached = JSON.parse(localStorage.getItem('user') || 'null');
      } catch {
        cached = null;
      }
      const sameUser = cached && (cached.id === me.id || normalizeEmail(cached.email) === normalizeEmail(me.email));

      // Preferir tipo/setor/cargo/unidade vindos do backend (cadastro), não inventar "usuario"/corporativo
      const multiUnidade = Boolean(me.canAccessAllUnidades);
      const userData = {
        id: me.id,
        email: me.email,
        nome: me.nome,
        tipo:
          me.tipo ||
          (sameUser && cached.tipo) ||
          (me.role === 'ADMIN' ? 'admin' : me.role === 'CORPORATE_MANAGER' ? 'gerente_corporativo' : 'usuario'),
        role: me.role,
        setor: me.setor != null ? me.setor : sameUser ? cached.setor || null : null,
        cargo: me.cargo != null ? me.cargo : sameUser ? cached.cargo || null : null,
        unidade: me.unidade || me.tenantCodigo || (sameUser ? cached.unidade : null) || null,
        tenantCodigo: me.tenantCodigo || me.unidade || null,
        canAccessAllUnidades: multiUnidade,
        substituicoesAtivas: Array.isArray(me.substituicoesAtivas) ? me.substituicoesAtivas : [],
      };
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      if (me.id) await buscarFotoPerfil(me.id);
      const unidRes = await apiFetch('/api/me/unidades');
      if (!unidRes.ok) {
        setUnidades([]);
        setUnidadeSelecionada(null);
        setSessionLoading(false);
        return;
      }
      const data = await unidRes.json();
      setUnidades(data);
      const chosen = pickUnidadeDaLista(data, {
        tenantCodigo: userData.tenantCodigo,
        canAccessAllUnidades: multiUnidade
      });
      setUnidadeSelecionada(chosen);
      if (chosen?.codigo) {
        localStorage.setItem('unidadeSelecionada', chosen.codigo);
      }
    } catch (err) {
      console.error('Erro ao restaurar sessão:', err);
      setToken(null);
      setUser(null);
    } finally {
      setSessionLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessionAndUnidades();
  }, [loadSessionAndUnidades]);

  useEffect(() => {
    const codigo = unidadeSelecionada?.codigo;
    if (!codigo) {
      const legacy = localStorage.getItem(ADMINS_KEY);
      if (legacy) {
        try { setAdmins(JSON.parse(legacy)); } catch { /* ignore */ }
      }
      return;
    }
    const saved = getUnitStorageJSON(ADMINS_KEY, codigo, null);
    if (saved) {
      setAdmins(saved);
      return;
    }
    const legacy = localStorage.getItem(ADMINS_KEY);
    if (legacy) {
      try {
        const parsed = JSON.parse(legacy);
        setAdmins(parsed);
        setUnitStorageJSON(ADMINS_KEY, codigo, parsed);
      } catch { /* ignore */ }
    }
  }, [unidadeSelecionada?.codigo]);

  // Aliases legados derivados da role (sem segundo motor de permissões)
  useEffect(() => {
    if (user && (user.tipo || user.role || user.email)) {
      const role = getUserRole(user);
      const tipo = user.tipo || '';
      const email = normalizeEmail(user.email);
      const ehCoordFin = role === ROLES.FISCAL_MONITOR || isCoordenadorFinanceiroUser(user);
      const ehAnalistaFin = role === ROLES.FINANCEIRO_ANALISTA || isAnalistaFinanceiroUser(user);

      setIsCoordenadorFinanceiro(ehCoordFin);
      setIsAnalistaFinanceiro(ehAnalistaFin);
      setIsAdmin(role === ROLES.SUPER_ADMIN || tipo === 'admin' || user.role === 'ADMIN' || admins.includes(email));
      // Equipe financeira (ações de pagamento) inclui coordenador/analista financeiro
      setIsFinanceiro(
        role === ROLES.FINANCEIRO ||
          role === ROLES.FINANCEIRO_ANALISTA ||
          tipo === 'financeiro' ||
          ehCoordFin ||
          ehAnalistaFin
      );
      // isFiscal = fiscal setorial restrito OU coordenador financeiro (fiscal do Financeiro)
      setIsFiscal(role === ROLES.FISCAL || role === ROLES.FISCAL_MONITOR || tipo === 'fiscal');

      const ehCargoCorporativo =
        role === ROLES.GLOBAL_VIEWER ||
        user.role === 'CORPORATE_MANAGER' ||
        verificarCargoCorporativo(tipo, user.cargo);
      setIsCargoCorporativo(ehCargoCorporativo);
      setCargoCorporativo(ehCargoCorporativo ? (user.cargo || 'Gerente Corporativo') : null);

      // Setor operacional do fiscal: coordenador financeiro usa setor cadastrado (Financeiro)
      if ((role === ROLES.FISCAL || role === ROLES.FISCAL_MONITOR || tipo === 'fiscal') && user.setor) {
        setFiscalSetor(user.setor);
      } else if (role === ROLES.SECTOR_VIEWER && user.setor) {
        setFiscalSetor(user.setor);
      } else {
        setFiscalSetor(null);
      }

      const fiscalEspecial = verificarFiscaisEspeciais(user.email);
      setIsFiscalEspecial(!!fiscalEspecial || ehCoordFin);
      setIsFiscalFinanceiro(fiscalEspecial === 'fiscal_financeiro' || ehCoordFin);
      setIsFiscalGerenciaAdm(fiscalEspecial === 'fiscal_gerencia_adm');
    } else {
      const userEmail = normalizeEmail(user?.email || user);
      setIsAdmin(userEmail ? admins.includes(userEmail) : false);
      setIsFinanceiro(false);
      setIsFiscal(false);
      setIsCargoCorporativo(false);
      setCargoCorporativo(null);
      setFiscalSetor(null);
      setIsFiscalEspecial(false);
      setIsFiscalFinanceiro(false);
      setIsFiscalGerenciaAdm(false);
      setIsCoordenadorFinanceiro(false);
      setIsAnalistaFinanceiro(false);
    }
  }, [user, admins]);

  useEffect(() => {
    if (user && (user.assinatura_arquivo || user.assinatura_nome || user.assinatura_cpf)) {
      setAssinatura({
        nome: user.assinatura_nome || null,
        cpf: user.assinatura_cpf || null,
        arquivoPath: user.assinatura_arquivo || null,
        atualizadoEm: user.assinatura_atualizada_em || null
      });
    } else {
      setAssinatura(null);
    }
  }, [user]);

  const login = async (email, senha) => {
    try {
      const response = await fetch(`${API_CONFIG.BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: (email || '').trim().toLowerCase(), senha: senha != null ? String(senha) : '' })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return false;
      }
      return applySessionFromToken(data.token, data.user);
    } catch (error) {
      console.error('Erro no login:', error);
      return false;
    }
  };

  /**
   * Aplica sessão SGC a partir do token (login senha ou callback Google).
   * Tokens Gmail NÃO transitam pelo front.
   */
  const applySessionFromToken = async (sessionToken, userHint = null) => {
    if (!sessionToken) return false;
    setToken(sessionToken);
    let u = userHint;
    if (!u) {
      const meRes = await apiFetch('/api/me');
      if (!meRes.ok) {
        setToken(null);
        return false;
      }
      u = await meRes.json();
    }
    const userData = {
      id: u.id,
      nome: u.nome,
      email: u.email,
      tipo: u.tipo,
      setor: u.setor,
      cargo: u.cargo,
      unidade: u.unidade || u.tenantCodigo || null,
      role: u.role,
      tenantCodigo: u.tenantCodigo || u.unidade || null,
      canAccessAllUnidades: Boolean(u.canAccessAllUnidades),
      gmailConnected: Boolean(u.gmailConnected),
      substituicoesAtivas: Array.isArray(u.substituicoesAtivas) ? u.substituicoesAtivas : [],
    };
    // Garante titulares ATIVOS mesmo se o login não trouxe o campo
    if (!userData.substituicoesAtivas.length) {
      try {
        const meRes = await apiFetch('/api/me');
        if (meRes.ok) {
          const me = await meRes.json();
          userData.substituicoesAtivas = Array.isArray(me.substituicoesAtivas)
            ? me.substituicoesAtivas
            : [];
          userData.gmailConnected = Boolean(me.gmailConnected);
        }
      } catch (_) {
        /* ignore */
      }
    }
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    const unidRes = await apiFetch('/api/me/unidades');
    if (unidRes.ok) {
      const list = await unidRes.json();
      setUnidades(list);
      const chosen = pickUnidadeDaLista(list, {
        tenantCodigo: userData.tenantCodigo,
        canAccessAllUnidades: userData.canAccessAllUnidades,
      });
      setUnidadeSelecionada(chosen);
      if (chosen?.codigo) {
        localStorage.setItem('unidadeSelecionada', chosen.codigo);
      }
    }
    if (userData.id) await buscarFotoPerfil(userData.id);
    return true;
  };

  const startGoogleLogin = () => {
    const origin = encodeURIComponent(window.location.origin);
    window.location.href = `${API_CONFIG.BACKEND_URL}/api/auth/google/start?mode=login&returnTo=/home&origin=${origin}`;
  };

  const connectGmail = async (returnTo = '/email') => {
    const origin = encodeURIComponent(window.location.origin);
    const res = await apiFetch(
      `/api/auth/google/connect-url?returnTo=${encodeURIComponent(returnTo)}&origin=${origin}`
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Não foi possível conectar o Gmail.');
    window.location.href = data.url;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setIsAdmin(false);
    setIsFinanceiro(false);
    setIsFiscal(false);
    setIsCargoCorporativo(false);
    setCargoCorporativo(null);
    setFiscalSetor(null);
    setFotoPerfil(null);
    setAssinatura(null);
    setIsFiscalEspecial(false);
    setIsFiscalFinanceiro(false);
    setIsFiscalGerenciaAdm(false);
    setIsCoordenadorFinanceiro(false);
    setIsAnalistaFinanceiro(false);
    setUnidades([]);
    setUnidadeSelecionada(null);
    localStorage.removeItem('user');
    localStorage.removeItem('unidadeSelecionada');
  };

  const addAdmin = async (email) => {
    const newAdmins = Array.from(new Set([...admins, email.trim().toLowerCase()]));
    setAdmins(newAdmins);
    const codigo = unidadeSelecionada?.codigo;
    if (codigo) setUnitStorageJSON(ADMINS_KEY, codigo, newAdmins);
    else localStorage.setItem(ADMINS_KEY, JSON.stringify(newAdmins));
  };

  const removeAdmin = async (email) => {
    const newAdmins = admins.filter(e => e !== email.trim().toLowerCase());
    setAdmins(newAdmins);
    const codigo = unidadeSelecionada?.codigo;
    if (codigo) setUnitStorageJSON(ADMINS_KEY, codigo, newAdmins);
    else localStorage.setItem(ADMINS_KEY, JSON.stringify(newAdmins));
  };

  // --- Helpers RBAC (fonte única: rbacRole / getUserRole) ---

  const isGlobalUser = useCallback(() => {
    const role = rbacRole || getUserRole(user);
    return (
      role === ROLES.SUPER_ADMIN ||
      role === ROLES.GLOBAL_VIEWER ||
      Boolean(user?.canAccessAllUnidades) ||
      user?.role === 'CORPORATE_MANAGER'
    );
  }, [rbacRole, user]);

  /** Troca de unidade só para quem tem permissão multi-unidade. */
  const canSwitchUnidade = useCallback(() => isGlobalUser(), [isGlobalUser]);

  const handleSetUnidadeSelecionada = (unidade) => {
    if (!canSwitchUnidade()) return;
    setUnidadeSelecionada(unidade);
    if (unidade?.codigo) localStorage.setItem('unidadeSelecionada', unidade.codigo);
  };

  /** Aprovações / fluxos gerais (não confundir com edição operacional). */
  const canAuthorize = useCallback(() => {
    const role = rbacRole || getUserRole(user);
    return (
      role === ROLES.SUPER_ADMIN ||
      role === ROLES.GLOBAL_VIEWER ||
      role === ROLES.LOCAL_APPROVER ||
      role === ROLES.LOCAL_ADMIN
    );
  }, [rbacRole, user]);

  /**
   * Quem participa de autorização no fluxo de contratação
   * (Fiscal inicia; Contratos/Diretoria/Corporativo autorizam).
   * FINANCEIRO setorial e SECTOR_VIEWER NÃO autorizam.
   */
  const canAuthorizeContratacao = useCallback(() => {
    const role = rbacRole || getUserRole(user);
    return (
      role === ROLES.SUPER_ADMIN ||
      role === ROLES.FISCAL ||
      role === ROLES.FISCAL_MONITOR ||
      role === ROLES.GLOBAL_VIEWER ||
      role === ROLES.LOCAL_APPROVER ||
      role === ROLES.LOCAL_ADMIN
    );
  }, [rbacRole, user]);

  /**
   * Controle de módulos (menu + rotas). Backend deve revalidar.
   */
  const hasPermission = useCallback((modulo) => {
    const role = rbacRole || getUserRole(user);
    if (!role) return false;
    if (role === ROLES.SUPER_ADMIN) return true;

    const key = String(modulo || '').trim().toLowerCase();

    switch (key) {
      case 'configuracao':
      case 'configuracoes':
      case 'configurações':
        // SUPER_ADMIN (early return) + LOCAL_ADMIN (Centro de Comando da unidade)
        return role === ROLES.LOCAL_ADMIN;

      case 'prestadores':
        return role === ROLES.LOCAL_ADMIN;

      case 'assinaturas':
      case 'assinatura':
        // Página Assinatura Digital disponível para todos os usuários autenticados.
        // Isolamento multiunidade permanece no backend (tenant).
        return true;

      case 'gestaousuarios':
      case 'gestao_usuarios':
      case 'gestão de usuários':
        return role === ROLES.LOCAL_ADMIN;

      case 'contratos':
        return [
          ROLES.GLOBAL_VIEWER,
          ROLES.LOCAL_APPROVER,
          ROLES.LOCAL_ADMIN,
          ROLES.LOCAL_OPERATOR,
          ROLES.FINANCEIRO,
          ROLES.FISCAL_MONITOR,
          ROLES.FINANCEIRO_ANALISTA,
          ROLES.FISCAL,
          ROLES.SECTOR_VIEWER,
        ].includes(role);

      case 'notasfiscais':
      case 'notas_fiscais':
      case 'notas fiscais':
        return [
          ROLES.GLOBAL_VIEWER,
          ROLES.LOCAL_APPROVER,
          ROLES.LOCAL_ADMIN,
          ROLES.LOCAL_OPERATOR,
          ROLES.FINANCEIRO,
          ROLES.FISCAL_MONITOR,
          ROLES.FINANCEIRO_ANALISTA,
          ROLES.FISCAL,
          ROLES.SECTOR_VIEWER,
        ].includes(role);

      case 'juridico':
      case 'jurídico':
      case 'oficios':
      case 'ofícios': {
        // Setor/cargo Jurídico (com ou sem acento) libera — ex.: fiscal do Jurídico.
        const norm = (s) =>
          String(s || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
        const blob = `${norm(user?.setor)} ${norm(user?.cargo)} ${norm(user?.tipo)}`;
        if (blob.includes('jurid')) return true;
        return [
          ROLES.LOCAL_ADMIN,
          ROLES.LOCAL_APPROVER,
          ROLES.LOCAL_OPERATOR,
          ROLES.GLOBAL_VIEWER,
        ].includes(role);
      }

      case 'dashboard':
      case 'fiscais':
      case 'fiscais e setores':
      case 'avaliacao':
      case 'avaliacao_fornecedores':
      case 'regulamentos':
      case 'prestacao_contas':
      case 'lgpd':
      case 'perfil':
        return true;

      default:
        return role !== ROLES.SECTOR_VIEWER || key === 'home';
    }
  }, [rbacRole, user]);

  const canViewAllNotas = () => {
    const role = rbacRole || getUserRole(user);
    return (
      isAdmin ||
      isFinanceiro ||
      isFiscalEspecial ||
      canMonitorAllSetoresRole(role)
    );
  };

  const canEditNota = () => {
    return isAdmin || rbacRole === ROLES.SUPER_ADMIN || rbacRole === ROLES.LOCAL_ADMIN;
  };

  const canAddComprovante = () => {
    return (
      isAdmin ||
      isFinanceiro ||
      isFiscalFinanceiro ||
      isCoordenadorFinanceiro ||
      isAnalistaFinanceiro ||
      rbacRole === ROLES.SUPER_ADMIN ||
      rbacRole === ROLES.FINANCEIRO_ANALISTA ||
      rbacRole === ROLES.FISCAL_MONITOR
    );
  };

  const canAddAvaliacao = (notaSetor) => {
    if (isAdmin || rbacRole === ROLES.SUPER_ADMIN || rbacRole === ROLES.LOCAL_ADMIN) return true;
    // Coordenador financeiro: fiscal só do setor Financeiro (cadastrado)
    if (isCoordenadorFinanceiro || rbacRole === ROLES.FISCAL_MONITOR) {
      if (!fiscalSetor) return false;
      return normalizeText(notaSetor) === normalizeText(fiscalSetor);
    }
    if (isFiscalEspecial && fiscalSetor) return notaSetor === fiscalSetor;
    if (isFiscal && fiscalSetor && rbacRole === ROLES.FISCAL) return notaSetor === fiscalSetor;
    return false;
  };

  const canMarcarComoPaga = () => {
    return (
      isAdmin ||
      isFinanceiro ||
      isFiscalFinanceiro ||
      isCoordenadorFinanceiro ||
      isAnalistaFinanceiro ||
      rbacRole === ROLES.SUPER_ADMIN ||
      rbacRole === ROLES.FINANCEIRO_ANALISTA ||
      rbacRole === ROLES.FISCAL_MONITOR
    );
  };

  const canEnviarContabilidade = () => {
    return (
      isAdmin ||
      isFinanceiro ||
      isCoordenadorFinanceiro ||
      isAnalistaFinanceiro ||
      rbacRole === ROLES.SUPER_ADMIN
    );
  };

  /** Monitor financeiro: não edita contratos (leitura/monitoramento). */
  const canEditContratos = () => {
    if (rbacRole === ROLES.GLOBAL_VIEWER) return false;
    if (rbacRole === ROLES.FISCAL_MONITOR || rbacRole === ROLES.FINANCEIRO_ANALISTA) return false;
    if (isCoordenadorFinanceiro || isAnalistaFinanceiro) return false;
    return !isCargoCorporativo && (isAdmin || isFinanceiro || rbacRole === ROLES.LOCAL_ADMIN || rbacRole === ROLES.LOCAL_OPERATOR);
  };

  const canEditPrestadores = () => {
    return hasPermission('Prestadores');
  };

  const canAccessConfiguracao = () => {
    return hasPermission('Configuracao');
  };

  const atualizarFotoPerfil = async (novaFoto) => {
    setFotoPerfil(novaFoto);
    if (user) {
      const updatedUser = { ...user, fotoPerfil: novaFoto };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const atualizarAssinaturaDados = (dados) => {
    setAssinatura(dados);
    if (user) {
      const updatedUser = {
        ...user,
        assinatura_nome: dados?.nome || null,
        assinatura_cpf: dados?.cpf || null,
        assinatura_arquivo: dados?.arquivoPath || null,
        assinatura_atualizada_em: dados?.atualizadoEm || new Date().toISOString()
      };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      sessionLoading,
      rbacRole,
      substituicoesAtivas,
      substituindoLabel,
      isAdmin,
      isFinanceiro,
      isFiscal,
      isCargoCorporativo,
      cargoCorporativo,
      fiscalSetor,
      fotoPerfil,
      assinatura,
      isFiscalEspecial,
      isFiscalFinanceiro,
      isFiscalGerenciaAdm,
      isCoordenadorFinanceiro,
      isAnalistaFinanceiro,
      admins,
      unidades,
      unidadeSelecionada,
      setUnidadeSelecionada: handleSetUnidadeSelecionada,
      login,
      applySessionFromToken,
      startGoogleLogin,
      connectGmail,
      logout,
      addAdmin,
      removeAdmin,
      atualizarAssinatura: atualizarAssinaturaDados,
      getUserRole: () => getUserRole(user),
      isGlobalUser,
      canSwitchUnidade,
      canAuthorize,
      canAuthorizeContratacao,
      hasPermission,
      canViewAllNotas,
      canEditNota,
      canAddComprovante,
      canAddAvaliacao,
      canMarcarComoPaga,
      canEnviarContabilidade,
      canEditContratos,
      canEditPrestadores,
      canAccessConfiguracao,
      atualizarFotoPerfil,
      atualizarAssinaturaDados
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  return ctx;
}

export default AuthContext;
